// React + Tailwind CSS version of Shaundju Academy Landing Page
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';

import Success from './pages/Success';
import Failed from './pages/Failed';
import Expired from './pages/Expired';
import Checkout from './pages/Checkout';
import Payment from './pages/Payment';
import mobileLogos from './assets/logos_mobile.png';
import desktopLogos from './assets/logos_desktop.png';
import founderImage from './assets/photo_header_new.png';
import founderImageMobile from './assets/photo_header_mobile_new.png';
import logoHeader from './assets/logo_header.png';
import logoFooter from './assets/logo_footer.png';
import bannerBottom from './assets/banner_bottom.png';
import testimonial1 from './assets/testi01.png';
import testimonial2 from './assets/testi02.png';
import testimonial3 from './assets/testi03.png';
import testimonial4 from './assets/testi04.png';
import testimonial5 from './assets/testi05.png';
import testimonial6 from './assets/testi06.png';
import testimonial7 from './assets/testi07.png';
import testimonial8 from './assets/testi08.png';
import promoteImage from './assets/promote.jpg';
import promoteImageMobile from './assets/promote_mobile.jpg';
import linkedinVideo from './assets/linkedin.mp4';
import salaryImage from './assets/gaji200jt.png';
import quotationIcon from './assets/icon_quotation.png';
import whatsappIcon from './assets/icon_wa.png';
import instagramIcon from './assets/icon_ig.png';
import iconYes from './assets/icon_yes.png';


// Add the following at the top of the file, after the imports
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Add batch configuration
const batch_config = {
  batch: 10,
  startDate: '2025-10-09T00:00:00',
  deadline: (() => {
    const now = new Date();
    // Get tomorrow's date in WIB
    const tomorrow = new Date(now.getTime() + (24 * 60 * 60 * 1000));
    const year = tomorrow.getFullYear();
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const day = String(tomorrow.getDate()).padStart(2, '0');
    // Return tomorrow at midnight in WIB format
    return `${year}-${month}-${day}T00:00:00`;
  })(),
  sessions: [
    {
      number: 2,
      date: '2025-10-18T10:00:00',
      title: 'Live Session M2'
    },
    {
      number: 3,
      date: '2025-10-25T10:00:00',
      title: 'Live Session M3'
    },
    {
      number: 5,
      date: '2025-11-01T10:00:00',
      title: 'Live Session M5'
    },
    {
      number: 6,
      date: '2025-11-08T10:00:00',
      title: 'Live Session M6'
    }
  ]
};



// Add meta tags for viewport control
const MetaTags = () => {
  useEffect(() => {
    // Add meta tags dynamically
    const meta = document.createElement('meta');
    meta.name = 'viewport';
    meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
    document.head.appendChild(meta);

    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  return null;
};

interface FormData {
  fullName: string;
  email: string;
  whatsapp: string;
  selectedClass: string;
}

// Add CountdownTimer component
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      // Parse deadline as WIB time
      // The deadline string represents WIB time, so we need to treat it as such
      const deadlineDate = new Date(batch_config.deadline + '+07:00'); // Add WIB timezone
      const deadline = deadlineDate.getTime();
      const now = new Date().getTime();
      const distance = deadline - now;

      if (distance < 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
      };
    };

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    // Update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []); // Remove the dependency since batch_config is constant

  return (
    <div className="flex justify-center gap-4 mt-4">
      <div className="text-center">
        <div className="text-2xl font-bold text-red-600">{String(timeLeft.days).padStart(2, '0')}</div>
        <div className="text-sm text-gray-600">Days</div>
      </div>
      <div className="text-center">
        <div className="text-2xl font-bold text-red-600">{String(timeLeft.hours).padStart(2, '0')}</div>
        <div className="text-sm text-gray-600">Hours</div>
      </div>
      <div className="text-center">
        <div className="text-2xl font-bold text-red-600">{String(timeLeft.minutes).padStart(2, '0')}</div>
        <div className="text-sm text-gray-600">Minutes</div>
      </div>
      <div className="text-center">
        <div className="text-2xl font-bold text-red-600">{String(timeLeft.seconds).padStart(2, '0')}</div>
        <div className="text-sm text-gray-600">Seconds</div>
      </div>
    </div>
  );
};

// Add helper function to format date
const formatDate = (dateString: string, withTime: boolean = true) => {
  const date = new Date(dateString);
  const days = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
  const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
  
  const day = days[date.getDay()];
  const month = months[date.getMonth()];
  const dateNum = date.getDate();
  
  if (!withTime) {
    return `${day}, ${dateNum} ${month}`;
  }
  
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  const formattedHours = hours % 12 || 12;
  
  return `${day}, ${dateNum} ${month} ${formattedHours}:${minutes.toString().padStart(2, '0')}${ampm}`;
};

const HomePage = () => {
  const navigate = useNavigate();
  const [expandedFaqs, setExpandedFaqs] = useState<number[]>([]);
  const [formData, setFormData] = useState<FormData>(() => {
    // Try to get saved form data from localStorage
    const savedData = localStorage.getItem('sjaFormData');
    return savedData ? JSON.parse(savedData) : {
      fullName: '',
      email: '',
      whatsapp: '',
      selectedClass: 'Penetrate Executive Status & Triple Digit Salary'
    };
  });
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({
    fullName: '',
    email: '',
    whatsapp: '',
    selectedClass: ''
  });

  
  useEffect(() => {
    // Initialize Google Analytics
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=G-5RKEYS68FE`;
    script.async = true;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', 'G-5RKEYS68FE', {
      send_page_view: true,
      page_title: 'Shaun Dju Academy',
      page_location: window.location.href,
      page_path: window.location.pathname
    });
  }, []);

  const trackPaymentClick = () => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'payment_button_click', {
        'event_category': 'engagement',
        'event_label': 'checkout_form'
      });
    }
  };



  const classes = [
    { 
      title: '2x Salary in 3 Years PLUS 5x Salary in 10 years',
      audience: 'First Jobbers',
      price: 'Rp1.950.000',
      normal: 'Rp2.500.000',
      modules: '4 Modules',
      features: [
        'Access to Module 1, 2, 3, 4',
        'Lifetime access for Module Videos, previous batch Live Session Recordings',
        'Open Q&A anytime via private Discord Community',
        'Personalized Homework Review by Shaun Dju'
      ]
    },
    { 
      title: 'Penetrate Executive Status & Triple Digit Salary',
      audience: 'Managers',
      price: 'Rp2.950.000',
      normal: 'Rp3.500.000',
      modules: '5 Modules',
      features: [
        'Access to Module 1, 2, 3, 4, 5',
        'Lifetime access for Module Videos, previous batch Live Session Recordings',
        'Open Q&A anytime via private Discord Community',
        'Personalized Homework Review by Shaun Dju'
      ]
    },
  ];

  const testimonials = [
    {
      name: "Harriet",
      title: "ex-Singapore Management Consultant",
      quote: "Most importantly Shaun showed me how to treat clients as real people instead of as a means to finish the project, I never had a doubt that Shaun is a good boss.",
      photo: testimonial1
    },
    {
      name: "Thomas",
      title: "INSEAD MBA Candidate",
      quote: "Apa yang Shaun ngomongin itu bener semua brokkk, apalagi yang project management, client relationship, dll.",
      photo: testimonial2
    },
    {
      name: "Filbert",
      title: "Seasoned Banker",
      quote: "The structured problem solving I learned from Shaun is a skill that you can take anywhere with you.",
      photo: testimonial3
    },
    {
      name: "Jihad",
      title: "Strategy at Pluang, ex-Shopee",
      quote: "Shaun's introduction to framework really helped me because I was never taught that in non-business major, especially at a non-target Indo school.",
      photo: testimonial4
    },
    {
      name: "Vanessa",
      title: "Graduating Senior, Oxford University",
      quote: "Having a clear starting point of the types of slide I was able to design, was most helpful, jadi kebayang the different options we have to best present info/data & can work from there what intuitively makes sense.",
      photo: testimonial5
    },
    {
      name: "V",
      title: "Big Boss at Retail Startup",
      quote: "I'm an SJA batch 1.1, I learned the proper mindset, people management and development skills, that I am taking to my current role.",
      photo: testimonial6
    },
    {
      name: "Abdul",
      title: "Assistant Manager at Grab",
      quote: "I learned a lot dari training lo terutama cara berpikir kritis + framework buat solve issues. Kepake pas gue handle salah satu project B2B terbesar Grab di GrabFood. So thank you ya Coach Shaun.",
      photo: testimonial7
    },
    {
      name: "Cyndi",
      title: "Final Year Student, NUS Singapore",
      quote: "From observing your work style I really admired how you work very gercep and how you handle clients juga, something that I wanna work towards!",
      photo: testimonial8
    },
  ];
  

  const benefits = [
    {
      title: 'Di-Promote 3x dalam 5 tahun',
      description: 'Dari analyst jadi project manager dalam 5 tahun. Karena kerja keras aja nggak cukup - lo harus tau cara main nya.',
      media: promoteImage,
      type: 'image'
    },
    {
      title: 'Bajakan LinkedIn banyak',
      description: 'Setelah corporate personal branding bener, perekrut mulai datang sendiri. Lo nggak lagi cari kerja — kerja yang cari lo.',
      media: linkedinVideo,
      type: 'video'
    },
    {
      title: 'Gaji 200jt',
      description: 'Di umur 30 sudah merasakan gaji 200jt. Bukan karena S2 luar negeri. Bukan karena ordal. Tapi karena ngerti cara main di dunia korporat.',
      media: salaryImage,
      type: 'image'
    },
  ];

  const modules = [
    {
      number: 'Module 1',
      name: 'Mindset Karir Stuck vs Anak Kesayangan Kantor',
      description: 'Kenapa ada yang cepet banget naik, dan lo ngerasa invisible? Semua dimulai dari cara berpikir yang efektif — yang nggak ada yang pernah ngajarin elu.'
    },
    {
      number: 'Module 2',
      name: 'Skill Interview yang Bikin Lo Nggak Terus-terusan Menunggu Kabar Balik',
      description: 'Lo bakal ngerti pola pikir HR dan user — biar lo jadi kandidat rebutan, bukan cuma pelengkap shortlist.',
    },
    {
      number: 'Module 3',
      name: 'Excel & PPT Hacks yang Bikin Lo ngeliat Orang Lain kaya - Koq Ngebabu?',
      description: 'Gue ga pernah ajarin elu untuk copas hasil2 gue, melainkan mengarahakan elu untuk berkarya dan berkreatif sendiri',
    },
    {
      number: 'Module 4',
      name: 'Komunikasi yang Singkat, Padat, Nendang - itu semua bisa di Pelajari',
      description: '"Orang yang bisa ngomong terstruktur dan jelas itu karena bakat bawaan" — gue akan tekenin bawa quotes ini sebuah Mitos! Komunikasi itu sains, bukan seni yang antara lo punya atau nggak punya.'
    },
    {
      number: 'Module 5',
      name: 'Rubah Persepsi Tim dan Boss lo untuk di Fast-Track Promote',
      description: 'Belajar cara ngatur proyek dan orang, supaya lo dilihat sebagai calon manajer — bukan sekadar staff yang rajin.'
    },
  ];

  const faqs = [
    {
      question: "Apakah ini cocok untuk semua industri?",
      answer: "Yes. Skill yang diajarin di sini itu transferable. Mau lo kerja di bank, tech, FMCG, konsultan, atau startup — semua butuh komunikasi yang tajam, eksekusi yang solid, dan visibilitas yang tepat."
    },
    {
      question: "Apa bedanya sama kursus lain?",
      answer: "Shaun Dju Academy bukan kelas teori. Ini playbook yang udah gue uji di dunia nyata: dari naik pangkat, dapet gaji tinggi, sampai handle proyek strategis. Setiap modul dibikin untuk langsung bisa dipraktekin di kerjaan lo besok pagi."
    },
    {
      question: "Kalau gue nggak jago Excel atau PPT, masih bisa ikut?",
      answer: "Justru harus ikut. Di salah satu modul, gue ajarin lo slide & spreadsheet yang dipakai beneran di korporat — bukan yang diajarin di kampus. Lo akan keliatan 2–3 level di atas rekan kerja yang skill-nya gitu-gitu aja."
    },
    {
      question: "Ada garansi lulus langsung naik gaji?",
      answer: "Nggak ada garansi — tapi ada bukti. Banyak tembusan Shaun Dju Academy, dapet promosi, naik gaji, atau bahkan ditarik perusahaan lain. Tapi semua tergantung satu hal: lo mau eksekusi atau nggak."
    },
    {
      question: "Apa rekening resmi Shaun Dju Academy?",
      answer: "Pembayaran dapat dilakukan via payment gateway di website Shaun Dju Academy, atau dapat melakukan transfer ke nomor rekening resmi, Bank BCA (Account Number 7050446888) atas nama Akademi Jurus Maut."
    }
  ];

  const toggleFaq = (index: number) => {
    setExpandedFaqs(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    console.log('Input changed:', name, value);
    


    setFormData((prev: FormData) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCourseClick = (courseTitle: string) => {
    setFormData((prev: FormData) => ({
      ...prev,
      selectedClass: courseTitle
    }));
    // Course selected, will navigate on button click
  };





  return (
    <div className="bg-white text-[#162d26] font-lato overflow-x-hidden">
      <MetaTags />
            {/* Navbar */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#203D32] w-full">
              <div className="w-[80%] max-w-[1440px] mx-auto py-4">
                <div className="flex justify-between items-center">
                  <img 
                    src={logoHeader} 
                    alt="Shaun Dju Academy" 
                    className="h-10 md:h-12"
                  />
                  <button 
                    onClick={() => navigate('/checkout')}
                    className="bg-white text-[#203D32] font-semibold py-2 px-6 rounded-full hover:opacity-90 transition"
                  >
                    Join Now
                  </button>
                </div>
              </div>
            </nav>

            {/* Hero Section */}
            <section className="w-full pt-16 md:pt-24 text-center bg-[#203D32] text-white relative overflow-hidden">
              <div className="w-[80%] max-w-[1440px] mx-auto">
                {/* Hero Text Container */}
                <div className="grid md:grid-cols-2 gap-4 md:gap-8">
                  <div className="text-left">
                    <h1 className="text-4xl md:text-6xl font-bold mb-4 md:mb-6 leading-tight font-alegreya">
                      How to Become a Corporate Superstar
                    </h1>
                    <p className="text-xl md:text-2xl mb-4 md:mb-8">
                      Rubah nasibmu dengan Jurus Maut yang para tembusan Shaun Dju Academy pake untuk dapetin gaji triple digit.
                    </p>
                    <div className="hidden md:block">
                      <button 
                        onClick={() => navigate('/checkout')}
                        className="bg-white text-[#203D32] font-semibold text-lg py-3 md:py-4 px-6 md:px-8 rounded-full shadow-md hover:opacity-90 transition"
                      >
                        Join Now
                      </button>
                    </div>
                  </div>
                  <div className="relative flex items-end justify-center md:justify-end">
                    <img 
                      src={founderImage} 
                      alt="Shaun Dju" 
                      className="hidden md:block w-full max-w-[400px]"
                    />
                  </div>
                </div>
                
                {/* Button and Mobile Photo Container */}
                <div className="relative">
                  <div className="md:hidden flex justify-start">
                    <button 
                      onClick={() => navigate('/checkout')}
                      className="bg-white text-[#203D32] font-semibold text-lg py-3 md:py-4 px-6 md:px-8 rounded-full shadow-md hover:opacity-90 transition"
                    >
                      Join Now
                    </button>
                  </div>
                  <div className="md:hidden">
                    <img 
                      src={founderImageMobile} 
                      alt="Shaun Dju" 
                      className="w-full max-w-[360px] ml-auto"
                    />
                  </div>
                </div>
              </div>
            </section>
            {/* Trusted Professionals Section */}
            <section className="w-full bg-[#FCFAF7] py-16">
              <div className="w-[80%] mx-auto">
                <h2 className="text-2xl md:text-3xl text-center mb-12 font-alegreya">
                  Trusted by Corporate Superstars from
                </h2>
                <div className="flex justify-center">
                  <div className="w-full max-w-3xl h-32 flex items-center justify-center">
                    <img 
                      src={mobileLogos} 
                      alt="Trusted companies" 
                      className="w-full h-full object-contain md:hidden" 
                    />
                    <img 
                      src={desktopLogos} 
                      alt="Trusted companies" 
                      className="hidden md:block w-full h-full object-contain" 
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Why Section */}
            <section className="py-12 bg-white">
              <div className="w-[80%] mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-[#203D32] font-alegreya">Why Shaun Dju Academy?</h2>
                <div className="space-y-16">
                  {/* First benefit */}
                  <div className="flex flex-col md:flex-row-reverse gap-8 items-center">
                    <div className="flex-1 space-y-4">
                      <h3 className="text-3xl font-bold font-alegreya">Dulu Nunggu Arahan, Sekarang Hidup Sesuai Gaya Sendiri</h3>
                      <p className="text-gray-600 leading-relaxed text-xl">Awal karir, gue cuma disuruh-suruh. Semua kerjaan based on perintah. Tapi pelan-pelan gue belajar: kalau lo mau naik kelas, lo harus kelihatan punya value — bukan cuma rajin, tapi strategis. Dari yang tadinya cuma bikin analisa dan presentasi, sekarang gue yang ambil keputusan. Dari kerja buat bayar tagihan, sekarang gue punya kebebasan milih proyek, atur waktu, dan hidup dengan gaya yang gue mau. Semua itu bukan sulap. Ada caranya — dan itu Jurus Maut yang gue ajarin di Shaun Dju Academy.</p>
                    </div>
                    <div className="rounded-lg bg-white flex items-center justify-center overflow-hidden shadow-lg h-[80vh]">
                      <img 
                        src={promoteImageMobile} 
                        alt="Promotion journey"
                        className="h-full w-auto object-contain md:hidden"
                      />
                      <img 
                        src={promoteImage} 
                        alt="Promotion journey"
                        className="hidden md:block h-full w-auto object-contain"
                      />
                    </div>
                  </div>

                  {/* Second benefit */}
                  <div className="flex flex-col md:flex-row gap-8 items-center">
                    <div className="flex-1 space-y-4">
                      <h3 className="text-3xl font-bold font-alegreya">Bajakan LinkedIn Banyak</h3>
                      <p className="text-gray-600 leading-relaxed text-xl">Dulu gue yang kirim-kirim CV, sekarang malah recruiter yang DM duluan.
Setelah branding dan reputasi lo bener, lo nggak lagi ngejar-ngejar kerjaan — justru kerjaan yang ngejar lo.
Tembusan Shaun Dju Academy sekarang sering dapet tawaran dari startup, bank, sampe multinasional.
Gara-gara mereka keliatan "beda" di mata HR dan decision maker.</p>
                    </div>
                    <div className="rounded-lg bg-white flex items-center justify-center overflow-hidden shadow-lg">
                      <video 
                        className="h-auto w-auto max-h-[80vh] object-contain"
                        autoPlay 
                        muted 
                        loop 
                        playsInline
                      >
                        <source src={linkedinVideo} type="video/mp4" />
                      </video>
                    </div>
                  </div>

                  {/* Third benefit */}
                  <div className="flex flex-col md:flex-row-reverse gap-8 items-center">
                    <div className="flex-1 space-y-4">
                      <h3 className="text-3xl font-bold font-alegreya">Gaji 2 Miliar / tahun</h3>
                      <p className="text-gray-600 leading-relaxed text-xl">Umur 30, gue udah ngerasain gaji triple digit. Karena gue tahu gimana cara positioning diri, negosiasi gaji, dan dapetin proyek yang bikin gue nggak tergantikan.
Shaun Dju Academy bukan tentang motivasi — ini soal strategi karier yang beneran jalan.</p>
                    </div>
                    <div className="rounded-lg bg-white flex items-center justify-center overflow-hidden shadow-lg">
                      <img 
                        src={salaryImage} 
                        alt="Salary achievement"
                        className="h-auto w-auto max-h-[400px] object-contain"
                      />
                    </div>
                  </div>

                  {/* Fourth benefit */}
                  <div className="flex flex-col md:flex-row gap-8 items-center">
                    <div className="flex-1 space-y-4">
                      <h3 className="text-3xl font-bold font-alegreya">Dibuat Sesuai Perjalanan Lo</h3>
                      <p className="text-gray-600 leading-relaxed text-xl">Gue udah ngalamin semua fase yang lo hadapin sekarang — dari susah cari kerja sebagai fresh grad, bingung mau gimana stand-out di first job, bahkan sampe struggle tembus ke next level sebagai manager.
Di Shaun Dju Academy, lo bakal dapet materi yang sesuai dengan posisi lo sekarang — semua udah gue siapin dengan rapi.</p>
                    </div>
                    <div className="rounded-lg bg-white flex items-center justify-center overflow-hidden shadow-lg">
                      <div className="rounded-lg bg-white flex items-center justify-center overflow-hidden shadow-lg">
                        <iframe
                          src="https://www.instagram.com/reel/DKwgpvohYMk/embed"
                          className="w-full max-w-[400px] h-[600px]"
                          frameBorder="0"
                          scrolling="no"
                          allowTransparency
                          allow="encrypted-media"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* As Seen On Section */}
            <section className="py-12 bg-[#FCFAF7]">
              <div className="w-[80%] mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-[#203D32] font-alegreya">As Seen On</h2>
                <div className="max-w-4xl mx-auto">
                  <a 
                    href="https://youtu.be/lkzRlr2cC4o?si=rvEgwRC53eQ641k1" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block relative group cursor-pointer"
                  >
                    <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-xl shadow-lg">
                      <img 
                        src="https://img.youtube.com/vi/lkzRlr2cC4o/maxresdefault.jpg" 
                        alt="Shaun Dju Academy Video"
                        className="absolute top-0 left-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-30 transition-opacity duration-300" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-20 h-20 bg-white bg-opacity-90 rounded-full flex items-center justify-center group-hover:bg-opacity-100 transition-opacity duration-300">
                          <svg 
                            className="w-10 h-10 text-[#203D32]" 
                            viewBox="0 0 24 24" 
                            fill="currentColor"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            </section>

            {/* Testimonials Section */}
            <section className="py-12 bg-[#FCFAF7]">
              <div className="w-[80%] mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-[#203D32] font-alegreya">Testimonials</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {testimonials.map((testimonial, i) => (
                    <div key={i} className="bg-white rounded-xl shadow-sm p-8 flex flex-col">
                      <div className="flex gap-4 mb-8 pb-6 border-b border-gray-100 h-28">
                        <div className="w-20 h-20 flex-shrink-0 overflow-hidden rounded-full">
                          <img 
                            src={testimonial.photo} 
                            alt={testimonial.name} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex flex-col justify-center">
                          <h3 className="text-xl font-bold text-[#203D32]">{testimonial.name}</h3>
                          <p className="text-base text-[#758C6F]">{testimonial.title}</p>
                        </div>
                      </div>
                      <div className="mb-4">
                        <img 
                          src={quotationIcon} 
                          alt="Quote" 
                          className="w-8 h-8"
                          style={{ filter: 'brightness(0) saturate(100%) invert(19%) sepia(14%) saturate(1800%) hue-rotate(118deg) brightness(92%) contrast(89%)' }}
                        />
                      </div>
                      <p className="text-gray-600 text-lg">{testimonial.quote}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Quote Block */}
            <section className="py-20 text-center bg-[#FCFAF7] relative">
              <div className="w-[80%] mx-auto relative">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/4">
                  <img 
                    src={quotationIcon} 
                    alt="Quote" 
                    className="w-40 h-40 opacity-20"
                    style={{ filter: 'brightness(0) saturate(100%) invert(83%) sepia(13%) saturate(238%) hue-rotate(328deg) brightness(89%) contrast(90%)' }}
                  />
                </div>
                <blockquote className="text-2xl md:text-4xl font-medium max-w-4xl mx-auto space-y-8 text-[#203D32] font-alegreya relative">
                  <p className="leading-normal md:leading-normal">
                    Every morning in Africa, a gazelle wakes up.<br />The gazelle knows it must run faster than<br /> the fastest lion, or it'll get eaten.
                  </p>
                  <p className="leading-normal md:leading-normal">
                    Every morning in Africa, a lion wakes up.<br />The lion knows it must run faster than<br /> the slowest gazelle, or it'll starve.
                  </p>
                  <p className="leading-normal md:leading-normal">
                    It doesn't matter if you're the lion or the gazelle-<br />when the sun comes up, you better start running.
                  </p>
                </blockquote>
              </div>
            </section>

            {/* Modules Section */}
            <section className="py-12 bg-white">
              <div className="w-[80%] mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-[#203D32] font-alegreya">Modules</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {modules.map((module, i) => (
                    <div key={i} className="bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                      <div className="bg-[#203D32] text-white text-xl font-bold p-4 text-center">
                        {module.number}
                      </div>
                      <div className="p-8">
                        <h3 className="text-2xl font-bold mb-4 text-[#203D32]">{module.name}</h3>
                        <p className="text-gray-600 text-lg leading-relaxed">{module.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Live Sessions Schedule Section
            <section className="py-12 bg-[#FCFAF7]">
              <div className="w-[80%] max-w-[1440px] mx-auto">
                <div className="bg-white rounded-xl shadow-sm p-8 md:p-12">
                  <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center text-[#203D32]">
                    Shaun Dju Academy Batch {batch_config.batch} akan dimulai pada: {formatDate(batch_config.startDate, false)}
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                    {batch_config.sessions.map((session) => (
                      <div key={session.number} className="flex items-center gap-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-12 h-12 bg-[#203D32] rounded-full flex items-center justify-center text-white font-bold">
                          {session.number}
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-[#203D32]">{session.title}</h3>
                          <p className="text-gray-600">{formatDate(session.date, true)}</p>
                        </div>
                      </div>
                    ))}
                  </div>


                </div>
              </div>
            </section> */}

            {/* Courses Section */}
            <section className="py-12 text-center bg-white">
              <div className="w-[80%] mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-[#203D32] font-alegreya">Select your Course</h2>
                <div className="mt-6 mb-12 text-center">
                    <p className="text-xl font-semibold text-[#203D32]">
                      Harga Spesial akan berakhir pada: {formatDate(batch_config.deadline, false)}
                    </p>
                    <CountdownTimer />
                  </div>
                <div className="grid gap-8 md:grid-cols-2">
                  {classes.map((item, idx) => (
                    <div 
                      key={idx} 
                      className={`rounded-xl p-8 transition-all cursor-pointer border-2 ${
                        formData.selectedClass === item.title 
                          ? 'bg-[#203D32] text-white border-[#203D32]' 
                          : 'bg-white text-[#203D32] border-[#E5EFE7] hover:border-[#203D32]'
                      }`}
                      onClick={() => {
                        handleCourseClick(item.title);
                      }}
                    >
                      <h3 className="text-2xl font-bold mb-3">{item.title}</h3>
                      <p className="text-lg mb-2">{item.audience}</p>
                      <p className={`line-through text-lg ${
                        formData.selectedClass === item.title ? 'text-gray-300' : 'text-gray-400'
                      }`}>{item.normal}</p>
                      <p className="text-3xl font-semibold mb-6">{item.price}</p>
                      <p className={`text-lg mb-6 ${
                        formData.selectedClass === item.title ? 'text-gray-200' : 'text-gray-600'
                      }`}>{item.modules}</p>
                      
                      <ul className="text-left space-y-4 mb-8">
                        {item.features.map((feature, fidx) => (
                          <li key={fidx} className="flex items-center gap-3">
                            <img 
                              src={iconYes} 
                              alt="Included" 
                              className={`w-5 h-5 ${
                                formData.selectedClass === item.title ? 'brightness-200' : ''
                              }`}
                            />
                            <span className="text-lg">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <button 
                        className={`w-full text-lg font-semibold px-8 py-4 rounded-full transition ${
                          formData.selectedClass === item.title 
                            ? 'bg-white text-[#203D32] hover:bg-gray-100' 
                            : 'bg-[#E5EFE7] text-[#203D32] hover:bg-[#D1E3D5]'
                        }`}
                        onClick={() => {
                          handleCourseClick(item.title);
                          navigate('/checkout');
                        }}
                      >
                        Join Now
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </section>



            {/* FAQ Section */}
            <section className="py-12 bg-white">
              <div className="w-[80%] mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-[#203D32] font-alegreya">Frequently Asked Questions</h2>
                <div className="space-y-6 max-w-3xl mx-auto">
                  {faqs.map((faq, i) => (
                    <div 
                      key={i} 
                      className="border border-gray-200 rounded-lg overflow-hidden cursor-pointer transition-all duration-300"
                      onClick={() => toggleFaq(i)}
                    >
                      <div className="p-6 flex justify-between items-center">
                        <h4 className="text-xl font-semibold text-[#203D32]">{faq.question}</h4>
                        <svg 
                          className={`w-6 h-6 transform transition-transform duration-300 ${expandedFaqs.includes(i) ? 'rotate-180' : ''}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                      <div 
                        className={`overflow-hidden transition-all duration-300 ${
                          expandedFaqs.includes(i) 
                            ? 'max-h-96 opacity-100' 
                            : 'max-h-0 opacity-0'
                        }`}
                      >
                        <p className="text-lg text-gray-600 p-6 pt-0">{faq.answer}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
            
            {/* CTA Section */}
            <section className="py-12 md:py-20">
              <div className="w-[90%] md:w-[80%] mx-auto">
                <div 
                  className="rounded-2xl overflow-hidden relative h-[300px] md:h-[600px]"
                  style={{ 
                    backgroundImage: `url(${bannerBottom})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="absolute inset-0 bg-black bg-opacity-50">
                    <div className="w-[90%] md:w-[80%] mx-auto h-full flex items-center">
                      <div className="w-full md:w-[40%] space-y-4 md:space-y-6">
                        <h2 className="text-2xl md:text-4xl font-bold text-white">
                          Jangan tunggu naik gaji dari HR — ambil alih nasib karier lo sendiri.
                        </h2>
                        <p className="text-lg md:text-2xl text-white">
                          Shaun Dju Academy bukan buat semua orang, tapi buat lo yang serius mau jadi Corporate Superstar. Siap naik level?
                        </p>
                        <div>
                          <button 
                            onClick={() => navigate('/checkout')}
                            className="bg-white text-[#203D32] font-semibold text-base md:text-lg py-3 md:py-4 px-6 md:px-8 rounded-full shadow-md hover:opacity-90 transition w-full md:w-auto"
                          >
                            Join Now
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#FCFAF7] text-[#203D32] py-10 w-full">
              <div className="w-[80%] max-w-[1440px] mx-auto">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <img 
                      src={logoFooter} 
                      alt="Shaun Dju Academy" 
                      className="h-12 mb-4"
                    />
                    <p className="text-lg mb-2">PT Akademi Jurus Maut</p>
                    <p className="text-lg mb-4">Jl. Pantai Indah Utara 2 C1, Kapuk Muara, Jakarta Utara</p>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-4">Contact Us</h3>
                    <div className="flex items-center gap-4 mb-2">
                      <img 
                        src={instagramIcon} 
                        alt="Instagram" 
                        className="w-6 h-6"
                      />
                      <a 
                        href="https://instagram.com/shaundju" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-lg hover:underline"
                      >
                        @shaundju
                      </a>
                    </div>
                    <div className="flex items-center gap-4">
                      <img 
                        src={whatsappIcon} 
                        alt="WhatsApp" 
                        className="w-6 h-6"
                      />
                      <a 
                        href="https://wa.me/6287841393601" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-lg hover:underline"
                      >
                        +62 878 413 936 01
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </footer>


          </div>
  );
};

const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/payment" element={<Payment />} />
        <Route path="/payment-success" element={<Success />} />
        <Route path="/payment-failed" element={<Failed />} />
        <Route path="/payment-expired" element={<Expired />} />
      </Routes>
    </Router>
  );
};

export default App;